/**
 * Utility class which presents interfaces for selection, creation, and destruction of `ObjectLayer` instances and underlying `Layer` models.
 * @module app/views/map/ObjectLayerSelector
 */
import _ from "underscore";
import Field from "core/models/Field";
import FormInputView from "core/views/FormInputView";
import Layer from "app/models/Layer";
import LayerEditor from "app/views/layer/LayerEditor";
import $ from "jquery";
import LayerUploadView from "app/views/layer/LayerUploadView";
import ModalView from "core/views/ModalView";
import ObjectLayer from "app/models/map/ObjectLayer";
import auth from "app/auth";
import ErrorUtils from "app/utils/ErrorUtils";
import ArgHelper from "app/views/map/helpers/ArgHelper";
import modals from "core/views/modals";
import urlService from "app/UrlService";
import isMobile from "core/is-mobile";
import mapContext from "app/views/map/MapContext";

var defaultOptions = {
  newName: "New Layer", //name for creating new service
  fieldLabel: "Select a Layer", //label for service selection dropdown
  confirmText: "Next", //title of "accept" button
  title: "Layer Select", //title of confirmation dialog,
  createOnly: false, //true to indicate that selecting an existing layer should be disallowed.
  modalClass: "layer-selector",
  create: function () {}, //function to be called if a service is created. first argument is the object created
  filter: function (form) {
    return true;
  }, //filter function to be applied to individual layer models (layers) before presenting selection
  canCreate: false, //true to indicate whether to allow creation, otherwise false
};

var ObjectLayerSelector = {
  /**
   * Presents an interface for selecting an `ObjectLayer` instance.
   * @param {InternalMapService} serviceLayer The map service in which to find or create the layer.
   * @param {object} [options] The options hash.
   * @param {Boolean} options.createOnly True to indicate that selecting an existing layer should be disallowed.
   * @param {string} options.newName:'New Layer' Text for selection option used to indicate creation of a new layer is desired.
   * @param {string} options.fieldLabel:'Select a Layer' Label for selection control.
   * @param {string} options.confirmText:'Next' Label text for control used to confirm selection.
   * @param {string} options.title:'Layer Select' Label text for entire UI block.
   * @param {Boolean} options.canCreate True to indicate a layer can be created.
   * @param {Boolean} [options.noUpload] True to prevent users from being presented with an upload option
   * @param {Function} [options.filter] Filter function which, given a `Layer`, shall return `true` if the layer is desired in the list of selectable items, otherwise `false`.
   * @param {Function} [options.create] Function which shall be called if a layer is created *successfully*. The first argument is the `ObjectLayer` instance that was created.
   * @param  {Function} cb            A callback which shall expect two arguments. The first is an error, if any, and the second is the selected `ObjectLayer`, if no error occurred.
   */
  selectLayer: function (serviceLayer, cb) {
    var options = ArgHelper.parseOptions(arguments, 1, defaultOptions);

    cb = ArgHelper.parseCallback(arguments, 1);
    var layerSelectModel = {
      layer: "",
    };
    var objectLayers = {};
    /*layer id -> object Layer*/
    var layers = [];
    var canCreate = options.canCreate;

    serviceLayer.eachLayer(function (layer, parents) {
      if (options.filter(layer.featureLayer)) {
        objectLayers[layer.featureLayer.id] = layer;
        var item = {
          value: layer.featureLayer.id,
          text: layer.name,
        };

        for (var i = 0; i < parents.length; i++) {
          item.text = "-" + item.text;
        }

        layers.push(item);
      }
    }, true);

    if (canCreate) {
      if (options.createOnly) {
        layers = [];
      }

      if (!options.noUpload) {
        layers.unshift({
          value: "uplode",
          text: "Upload",
        });
      }
      layers.unshift({
        value: "",
        text: options.newName,
      });
    } else {
      layerSelectModel.layer = _.first(_.keys(objectLayers));
    }

    var layerField = new Field({
      id: "layer",
      name: "layer",
      label: options.fieldLabel,
      values: layers,
    });
    var layerSelect = new FormInputView({
      model: layerSelectModel,
      field: layerField,
    });

    //create a form to use if the user wants to create an ObjectLayer
    var layer = new Layer({
      hasGeometry: true,
    });

    layer.collection = serviceLayer.featureLayers;
    var editorView = new LayerEditor({
      model: layer,
    });

    var uploadView = new LayerUploadView({
      url: urlService.apiurl("/layers/upload") + "?token=" + auth.token,
      serviceId: serviceLayer.service.id,
    });

    layerSelect.on("change", function (value) {
      if (value === "" || !value) {
        if (canCreate) {
          editorView.$el.show();
        }

        uploadView.$el.hide();
      } else if (value === "uplode") {
        if (canCreate) {
          uploadView.$el.show();
        }

        editorView.$el.hide();
      } else {
        editorView.$el.hide();
        uploadView.$el.hide();
      }
    });

    layerSelect.render();
    editorView.render();
    uploadView.render();

    if (!canCreate) {
      editorView.$el.hide();
    }

    var content = layerSelect.$el.add(editorView.$el);

    content = content.add(uploadView.$el);
    uploadView.$el.hide();

    if (options.createOnly) {
      //layerSelect.$el.hide();
    }

    options.serviceLayer = serviceLayer;

    var deferred = new $.Deferred();

    var confirmOptions = {
      className: isMobile ? "btn-lg" : "",
    };

    if (_.isObject(options.confirmText)) {
      confirmOptions = _.extend(confirmOptions, options.confirmText);
    } else {
      confirmOptions.text = options.confirmText;
    }

    var modalView = modals.confirm(
      options.title,
      content,
      confirmOptions,
      function () {
        if (layerSelect.model.get("layer") === "") {
          mapContext
            .getOperation("add-layer-blank")
            .action(editorView, options)
            .then(function (model) {
              deferred.resolve(model);
            })
            .fail(function (model, response) {
              deferred.reject(
                ErrorUtils.normalizeError(
                  {
                    activity: "creating the layer",
                  },
                  response
                )
              );
            });
        } else if (layerSelect.model.get("layer") === "uplode") {
          mapContext
            .getOperation("add-layer-upload")
            .action(uploadView, options)
            .then(function (allLayers) {
              modalView.remove();
              deferred.resolve(allLayers);
            })
            .fail(function (err, response) {
              deferred.reject(
                ErrorUtils.normalizeError(
                  {
                    activity: "uploading the layer",
                  },
                  response
                )
              );
            });
        } else {
          deferred.resolve(objectLayers[layerSelect.model.get("layer")]);
        }
      },
      {
        text: "Cancel",
        className: isMobile ? "btn-lg" : "",
      },
      function () {
        deferred.reject(true);
      },
      options.modalClass
    );

    modalView.once(
      "hidden remove",
      _.once(function () {
        deferred
          .done(function (result) {
            cb(null, result);
          })
          .fail(function (err) {
            cb(err);
          });
      })
    );

    var createButton = modalView.buttons[1];

    layerSelect.on("change", function (value) {
      if (value) {
        //enable create button
        createButton.enable();
      } else {
        if (layer.get("name")) {
          //enable create button
          createButton.enable();
        } else {
          //disable create button
          createButton.disable();
        }
      }
    });

    layer.on("change:name", function (model, value) {
      if (value) {
        createButton.enable();
      } else {
        createButton.disable();
      }
    });

    if (layerSelectModel.layer) {
      createButton.enable();
    } else {
      createButton.disable();
    }
  },

  /**
   * Presents an interface for selecting a `Layer` instance.
   * @param {InternalMapService} serviceLayer The map service in which to find or create the layer.
   * @param {object} [options] The options hash.
   * @param {Boolean} options.createOnly True to indicate that selecting an existing layer should be disallowed.
   * @param {string} options.newName:'New Layer' Text for selection option used to indicate creation of a new layer is desired.
   * @param {string} options.fieldLabel:'Select a Layer' Label for selection control.
   * @param {string} options.confirmText:'Next' Label text for control used to confirm selection.
   * @param {string} options.title:'Layer Select' Label text for entire UI block.
   * @param {Boolean} options.canCreate True to indicate a layer can be created.
   * @param {Function} [options.filter] Filter function which, given a `Layer`, shall return `true` if the layer is desired in the list of selectable items, otherwise `false`.
   * @param {Function} [options.create] Function which shall be called if a layer is created *successfully*. The first argument is the `Layer` instance that was created.
   * @param  {Function} cb            A callback which shall expect two arguments. The first is an error, if any, and the second is the selected `Layer`, if no error occurred.
   */
  selectLayerModel: function (serviceLayer, cb) {
    var options = ArgHelper.parseOptions(arguments, 1, defaultOptions);

    cb = ArgHelper.parseCallback(arguments, 1);
    var create = options.create;
    var deferred = new $.Deferred();

    options.create = function () {
      deferred.done(function (layer) {
        create(serviceLayer, layer.featureLayer);
      });
    };

    ObjectLayerSelector.selectLayer(
      serviceLayer,
      options,
      function (err, layer) {
        if (err) {
          return cb(err);
        }

        deferred.resolve(layer);
        cb(null, layer.featureLayer);
      }
    );
  },

  /**
   * Generates an `ObjectLayer` instance from a given `Layer` model.
   * @param  {InternalMapService}   serviceLayer The service with which to associate the layer.
   * @param  {Layer}   layerModel   The model of the layer to generate.
   * @param  {Function} [cb]           A function which shall expect two arguments. The first is an error, if any, and the second is the `ObjectLayer` which was generated, if the operation was successful.
   * @return {ObjectLayer}                If `cb` is not specified, the `ObjectLayer` that was created.
   */
  createLayer: function (serviceLayer, layerModel, cb) {
    var objectLayers = {};
    /*form id -> object Layer*/

    serviceLayer.eachLayer(function (layer) {
      objectLayers[layer.featureLayer.id] = layer;
    }, true);

    var layer = new ObjectLayer({
      featureLayer: layerModel,
      parent: objectLayers[layerModel.getParentId()],
      serviceLayer: serviceLayer,
      features: layerModel.getItems(),
    });

    if (_.isFunction(cb)) {
      return cb(null, layer);
    } else {
      return layer;
    }
  },

  /**Prompts a user regarding the deletion of a layer model (Layer).
	 @param {Layer} layerModel The layer model to delete.
	 @param {string} typeName The word to use to describe the model type, e.g. "Form".
	 @param {Function} cb The callback to be called on success or error. If the first argument is `true`,
	 the operation was cancelled. If it is just "truthy", it contains an error message. Otherwise,
	 the operation succeeded.*/
  destroyLayer: function (layerModel, typeName, cb) {
    var modal;
    var CANCEL = "Cancel";
    var DELETE = "Delete";
    var DELETE_TITLE = DELETE + " " + typeName;
    var DELETE_PROMPT =
      "Would you like to permanently delete this layer?<br/>Deleting will <strong>permanently destroy</strong> layer data, geometry, and files.";
    var REALLY_DELETE_PROMPT =
      "Are you sure you want to <strong>permanently delete</strong> this layer? This action cannot be undone.";
    var REALLY_DELETE = "Permanently " + DELETE;

    modal = new ModalView({
      title: DELETE_TITLE,
      buttons: [
        {
          text: CANCEL,
          click: function () {
            modal.remove();
            cb(true);
          },
        },
        {
          text: DELETE,
          click: function () {
            modal.remove();
            modal = new ModalView({
              title: DELETE_TITLE,
              buttons: [
                {
                  text: CANCEL,
                  click: function () {
                    modal.remove();
                    cb(true);
                  },
                },
                {
                  text: REALLY_DELETE,
                  click: function () {
                    modal.remove();
                    layerModel.destroy({
                      success: function () {
                        cb();
                      },
                      error: function (model, response) {
                        cb(
                          ErrorUtils.normalizeError(
                            {
                              activity: "deleting the layer",
                            },
                            response
                          )
                        );
                      },
                    });
                  },
                  type: "danger",
                },
              ],
            });
            modal.render();
            modal.setContent(REALLY_DELETE_PROMPT);
            modal.show();
          },
          type: "danger",
        },
      ],
    });
    modal.render();
    modal.setContent(DELETE_PROMPT);
    modal.show();
  },
};

export default ObjectLayerSelector;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/map/ObjectLayerSelector.js
// module id = 348
// module chunks = 1